import { useState } from 'react';
import BlocoRecebedor from '@/pages/assistente-evento/components/BlocoRecebedor';
import { combinarDataHora } from '@/pages/assistente-evento/utils';
import { formatarValor } from '@/pages/evento/utils';
import CartaoIngresso from './CartaoIngresso';
import { nomeDeLote, novoIngresso, type Ingresso } from '../types';

function formatarDiaCurto(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function diaSeguinte(iso: string): string {
  const d = new Date(iso);
  d.setDate(d.getDate() + 1);
  return d.toISOString();
}

// Mostra em cinza o período de venda calculado: a pessoa só preenche o "até".
function resumoDeVenda(ingressos: Ingresso[], indice: number): string {
  const lote = ingressos[indice];
  const ehUltimo = indice === ingressos.length - 1;

  const fimIso = lote.aVendaAteData
    ? combinarDataHora(lote.aVendaAteData, lote.aVendaAteHora)
    : null;
  const fim = fimIso ? formatarDiaCurto(fimIso) : null;

  let inicio: string | null = null;
  if (lote.aVendaDeData) {
    const iniIso = combinarDataHora(lote.aVendaDeData, lote.aVendaDeHora);
    if (iniIso) inicio = formatarDiaCurto(iniIso);
  } else if (indice > 0) {
    const anterior = ingressos[indice - 1];
    const fimAnterior = anterior.aVendaAteData
      ? combinarDataHora(anterior.aVendaAteData, anterior.aVendaAteHora)
      : null;
    if (fimAnterior) inicio = formatarDiaCurto(diaSeguinte(fimAnterior));
  }

  if (indice === 0 && !lote.aVendaDeData) {
    return fim
      ? `À venda assim que você publicar, até ${fim}.`
      : 'À venda assim que você publicar.';
  }
  if (inicio && fim) return `À venda de ${inicio} a ${fim}.`;
  if (inicio) {
    return ehUltimo
      ? `À venda a partir de ${inicio} até o dia do evento.`
      : `À venda a partir de ${inicio}.`;
  }
  if (fim) return `À venda até ${fim}.`;
  if (ehUltimo) return 'Vale até o dia do evento.';
  return 'À venda assim que você publicar.';
}

interface PassoIngressosProps {
  ingressos: Ingresso[];
  eventoId: number | null;
  onChange: (ingressos: Ingresso[]) => void;
  onTemRecebedor: (tem: boolean) => void;
}

export default function PassoIngressos({
  ingressos,
  eventoId,
  onChange,
  onTemRecebedor,
}: PassoIngressosProps) {
  const [expandidos, setExpandidos] = useState<Set<number>>(new Set());
  const [ajudaAberta, setAjudaAberta] = useState(false);

  function alternar(indice: number) {
    setExpandidos((prev) => {
      const novo = new Set(prev);
      if (novo.has(indice)) novo.delete(indice);
      else novo.add(indice);
      return novo;
    });
  }

  function remover(indice: number) {
    onChange(ingressos.filter((_, i) => i !== indice));
  }

  function adicionar() {
    onChange([...ingressos, { ...novoIngresso(), nome: nomeDeLote(ingressos.length + 1) }]);
  }

  function mudar(indice: number, patch: Partial<Ingresso>) {
    onChange(ingressos.map((p, i) => (i === indice ? { ...p, ...patch } : p)));
  }

  const total = ingressos.reduce(
    (acc, i) => acc + (i.temLimite && i.vagas !== null ? i.vagas : 0),
    0,
  );
  const podeEntrar = ingressos.reduce(
    (acc, i) =>
      acc + (i.valorModo === 'pago' && i.temLimite && i.vagas !== null ? i.valorCentavos * i.vagas : 0),
    0,
  );

  const partes = [
    `${ingressos.length} ${ingressos.length === 1 ? 'lote' : 'lotes'}`,
    total > 0 ? `${total} ingressos` : 'sem limite',
  ];
  if (podeEntrar > 0) partes.push(`pode entrar até ${formatarValor(podeEntrar)}`);

  return (
    <div className="space-y-8">
      <div>
        <div className="flex items-center gap-2">
          <h2 className="text-2xl font-normal text-foreground-950">Ingressos</h2>
          <button
            type="button"
            onClick={() => setAjudaAberta(true)}
            aria-label="Não precisa ser por lote"
            className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-full border border-secondary-300 text-secondary-500 transition-colors hover:border-primary-400 hover:text-primary-600"
          >
            <i className="ri-question-line text-sm" />
          </button>
        </div>
        <p className="mt-1 text-sm text-secondary-500">
          O preço sobe a cada lote. Quando a data de um acaba, o próximo entra sozinho.
        </p>
      </div>

      <div id="lista-ingressos" className="space-y-4">
        {ingressos.map((p, i) => (
          <CartaoIngresso
            key={i}
            ingresso={p}
            indice={i}
            expandido={expandidos.has(i)}
            resumoVenda={resumoDeVenda(ingressos, i)}
            aoAlternar={() => alternar(i)}
            aoMudar={(patch) => mudar(i, patch)}
            aoRemover={() => remover(i)}
          />
        ))}
      </div>

      <button
        type="button"
        onClick={adicionar}
        className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg border-2 border-dashed border-secondary-300 bg-white px-4 py-4 text-[15px] font-medium text-primary-600 transition-colors hover:border-primary-400 hover:bg-background-100"
      >
        <span className="flex h-5 w-5 items-center justify-center">
          <i className="ri-add-line" />
        </span>
        Adicionar lote
      </button>

      <div className="rounded-lg border border-secondary-200 bg-background-50 px-4 py-3 text-sm text-foreground-800">
        {partes.join(' · ')}
      </div>

      <div id="bloco-recebedor">
        <BlocoRecebedor eventoId={eventoId} aoMudarTem={onTemRecebedor} />
      </div>

      {ajudaAberta && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          onClick={() => setAjudaAberta(false)}
        >
          <div
            className="w-full max-w-[500px] overflow-hidden rounded-lg border border-secondary-200 bg-white"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 border-b border-secondary-100 px-5 py-4">
              <h3 className="text-base font-medium text-foreground-950">Não precisa ser por lote</h3>
              <button
                type="button"
                onClick={() => setAjudaAberta(false)}
                aria-label="Fechar"
                className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-secondary-500 transition-colors hover:bg-background-100 hover:text-foreground-800"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="max-h-[70vh] space-y-4 overflow-y-auto px-5 py-4 text-sm text-foreground-800">
              <p>Se o preço não muda com o tempo, apague os lotes e crie por tipo:</p>
              <div className="space-y-1 text-secondary-600">
                <p>Inteira · R$ 40,00 · 200</p>
                <p>Meia · R$ 20,00 · 50</p>
                <p>Mesa para 4 · R$ 150,00 · 20</p>
              </div>
              <p>Os dois jeitos usam a mesma tela. O que muda é só o nome e a data que você põe.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}